/** Locale ids from AGENTS.md (day one + later). All remain loadable; picker is trimmed. */

export const LOCALE_IDS = [
  "en",
  "en-GB",
  "es",
  "pt-BR",
  "de",
  "fr",
  "it",
  "pl",
  "uk",
  "ru",
  "ko",
  "tl",
  "nl",
  "sv",
  "no",
  "da",
  "fi",
  "el",
  "ro",
  "hu",
  "cs",
  "id",
  "zh-Hans",
  "sw",
  "am",
  "vi",
  "af",
] as const;

/** Languages shown in the picker. Stubs (sw, am, vi) stay loadable but hidden. */
export const PICKER_LOCALE_IDS = [
  "en",
  "en-GB",
  "es",
  "pt-BR",
  "de",
  "fr",
  "it",
  "pl",
  "uk",
  "ru",
  "ko",
  "tl",
  "zh-Hans",
  "nl",
  "sv",
  "no",
  "da",
  "fi",
  "el",
  "ro",
  "hu",
  "cs",
  "id",
  "af",
] as const;

export type PickerLocaleId = (typeof PICKER_LOCALE_IDS)[number];

export type LocaleId = (typeof LOCALE_IDS)[number];

export const LOCALE_LABELS: Record<LocaleId, string> = {
  en: "English (US)",
  "en-GB": "English (UK)",
  es: "Español",
  "pt-BR": "Português (Brasil)",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
  pl: "Polski",
  uk: "Українська",
  ru: "Русский",
  ko: "한국어",
  tl: "Tagalog",
  nl: "Nederlands",
  sv: "Svenska",
  no: "Norsk",
  da: "Dansk",
  fi: "Suomi",
  el: "Ελληνικά",
  ro: "Română",
  hu: "Magyar",
  cs: "Čeština",
  id: "Bahasa Indonesia",
  "zh-Hans": "中文（简体）",
  sw: "Kiswahili",
  am: "አማርኛ",
  vi: "Tiếng Việt",
  af: "Afrikaans",
};

export function isLocaleId(value: string): value is LocaleId {
  return (LOCALE_IDS as readonly string[]).includes(value);
}

export function isPickerLocaleId(value: string): value is PickerLocaleId {
  return (PICKER_LOCALE_IDS as readonly string[]).includes(value);
}

/** Map saved / browser locales to a picker choice; fallback en. */
export function normalizePickerLocale(locale: LocaleId): PickerLocaleId {
  if (isPickerLocaleId(locale)) return locale;
  return "en";
}

/**
 * Guess a picker locale from CF-IPCountry (ISO alpha-2).
 * Used on first visit when nothing is saved in `un8.locale.v1`.
 */
export function localeFromCountry(country: string | null | undefined): LocaleId | null {
  if (!country) return null;
  const c = country.toUpperCase();
  if (c === "BR" || c === "PT") return "pt-BR";
  if (
    c === "MX" ||
    c === "ES" ||
    c === "CO" ||
    c === "AR" ||
    c === "CL" ||
    c === "PE" ||
    c === "UY" ||
    c === "VE" ||
    c === "EC" ||
    c === "GT" ||
    c === "CR" ||
    c === "PA" ||
    c === "BO" ||
    c === "PY" ||
    c === "DO" ||
    c === "HN" ||
    c === "SV" ||
    c === "NI" ||
    c === "CU"
  ) {
    return "es";
  }
  if (c === "DE" || c === "AT" || c === "LI") return "de";
  if (c === "CH") return "de";
  if (c === "KR") return "ko";
  if (c === "PH") return "tl";
  if (c === "CN" || c === "SG" || c === "TW" || c === "HK" || c === "MO") return "zh-Hans";
  if (c === "GB" || c === "IE" || c === "AU" || c === "NZ") return "en-GB";
  if (c === "FR" || c === "BE" || c === "LU" || c === "MC") return "fr";
  if (c === "IT") return "it";
  if (c === "PL") return "pl";
  if (c === "UA") return "uk";
  if (c === "RU") return "ru";
  if (c === "NL") return "nl";
  if (c === "ZA") return "af";
  if (c === "US" || c === "CA" || c === "NG" || c === "KE" || c === "GH" || c === "UG" || c === "ET" || c === "CD") {
    return "en";
  }
  return null;
}

/** Map browser language tags to our LocaleId. */
export function matchBrowserLocale(tag: string): LocaleId | null {
  const lower = tag.toLowerCase();
  if (lower.startsWith("en-gb") || lower.startsWith("en-au") || lower.startsWith("en-nz")) {
    return "en-GB";
  }
  if (lower.startsWith("en")) return "en";
  if (lower.startsWith("pt-br") || lower === "pt") return "pt-BR";
  if (lower.startsWith("es")) return "es";
  if (lower.startsWith("de")) return "de";
  if (lower.startsWith("fr")) return "fr";
  if (lower.startsWith("it")) return "it";
  if (lower.startsWith("pl")) return "pl";
  if (lower.startsWith("uk")) return "uk";
  if (lower.startsWith("ru")) return "ru";
  if (lower.startsWith("ko")) return "ko";
  if (lower.startsWith("tl") || lower.startsWith("fil")) return "tl";
  if (lower.startsWith("nl")) return "nl";
  if (lower.startsWith("sv")) return "sv";
  if (lower.startsWith("nb") || lower.startsWith("nn") || lower.startsWith("no")) return "no";
  if (lower.startsWith("da")) return "da";
  if (lower.startsWith("fi")) return "fi";
  if (lower.startsWith("el")) return "el";
  if (lower.startsWith("ro")) return "ro";
  if (lower.startsWith("hu")) return "hu";
  if (lower.startsWith("cs")) return "cs";
  if (lower.startsWith("id")) return "id";
  if (lower.startsWith("zh")) return "zh-Hans";
  if (lower.startsWith("sw")) return "sw";
  if (lower.startsWith("am")) return "am";
  if (lower.startsWith("vi")) return "vi";
  if (lower.startsWith("af")) return "af";
  return null;
}
